import { PresentationBundleKind } from '@/types/schema';

import { NEW_ROW_ID_SENTINEL } from '../../ClientPresentationDesigner.constants';
import { ClientPresentationV2Bundle } from '../../types/ClientPresentation.PresentationBundleType';
import {
  isEmptyKeyedListMutationOperations,
  mapKeyedListMutationOperations,
} from '../diff/mapKeyedListMutationOperations';
import {
  BundleConfigurationPayload,
  BundleSaveDescriptor,
  DiffBundleConfigurationArgs,
} from '../presentationSave.types';
import { canWriteOwnedConfig } from './flatConfigDescriptor';

/**
 * The shared shape of the two-level configurations: a title, a list of cards, and
 * a list of items inside each card.
 *
 * Six bundle kinds have it — trusts, businesses, wills, powers of attorney,
 * healthcare directives and asset distributions — and they differ only in what
 * their fields and payload slots are called. Each descriptor normalizes its own
 * field names into this shape, so the nesting is diffed once rather than six
 * times.
 */
export interface TwoLevelCard {
  id: string;
  /** The card's heading: a grantor's name, or a trust's or business's display
   *  name. */
  name: string;
  items: TwoLevelItem[];
}

export interface TwoLevelItem {
  id: string;
  label: string;
  body: string;
}

export interface NormalizedTwoLevelConfig {
  id?: string;
  displayName: string | null;
  itemsPerPage?: number;
  cards: TwoLevelCard[];
}

/** The item payloads are identical across all six kinds; only slots differ. */
export interface ItemCreate {
  create: {
    householdID: string;
    label: string;
    body: string;
    sortOrder: number;
  };
}

export interface ItemUpdate {
  id: string;
  update: { label?: string; body?: string; sortOrder?: number };
}

export interface CardItemsDiff {
  creates: ItemCreate[];
  updates: ItemUpdate[];
  removeIDs: string[];
}

/**
 * What a descriptor has to supply: how to read its configuration, and how to name
 * the slots its payloads go in.
 *
 * The slot builders are closures rather than slot names because assigning through
 * a key variable widens the write to the intersection of every slot's type.
 */
interface GeneratedBundleCardPayloadSpec<
  TCardCreate,
  TCardUpdate,
  TScalars = Record<string, never>,
> {
  field: keyof ClientPresentationV2Bundle;
  read: (
    bundle: ClientPresentationV2Bundle
  ) => NormalizedTwoLevelConfig | undefined;
  /**
   * Configuration-level scalars beyond the title, for the kinds that have any.
   * Null when none of them changed.
   *
   * Takes the bundles rather than the normalized configuration, because these
   * fields are exactly the ones the normalized shape does not carry — every kind
   * has different ones, and normalizing them would mean a shape with a hole in it.
   */
  diffScalars?: (
    previous: ClientPresentationV2Bundle,
    next: ClientPresentationV2Bundle
  ) => TScalars | null;
  /** A card the server does not have, with its items. */
  buildCardCreate: (args: {
    name: string;
    sortOrder: number;
    householdId: string;
    items: ItemCreate[];
  }) => TCardCreate;
  /** A card the server has, carrying only what changed about it. */
  buildCardUpdate: (args: {
    id: string;
    name: string | undefined;
    sortOrder: number | undefined;
    items: CardItemsDiff;
  }) => TCardUpdate;
  /** The whole configuration payload, in this kind's own slot. */
  buildPayload: (args: {
    id: string;
    displayName: { displayName?: string; clearDisplayName?: boolean } | null;
    itemsPerPage: { itemsPerPage: number } | null;
    scalars: TScalars | null;
    cardCreates: TCardCreate[];
    cardUpdates: TCardUpdate[];
    removeCardIDs: string[];
  }) => BundleConfigurationPayload;
}

function serverRowId(row: { id: string }): string | null {
  return row.id.startsWith(NEW_ROW_ID_SENTINEL) ? null : row.id;
}

/**
 * Diffs the items inside one card.
 *
 * Citations are neither compared nor sent: they are written by the backend during
 * generation and cannot be edited in the designer, so an item whose citations
 * alone differ produces no write.
 */
function diffItems(
  previous: TwoLevelItem[],
  next: TwoLevelItem[],
  householdId: string
): CardItemsDiff {
  const wasAt = new Map(previous.map((item, index) => [item.id, index]));

  return mapKeyedListMutationOperations<TwoLevelItem, ItemCreate, ItemUpdate>({
    previousItems: previous,
    currentItems: next,
    getId: serverRowId,
    toCreate: (item, sortOrder) => ({
      create: {
        householdID: householdId,
        label: item.label,
        body: item.body,
        sortOrder,
      },
    }),
    toUpdate: ({ previous: was, next: is, sortOrder }) => {
      const update: ItemUpdate['update'] = {};
      if (was.label !== is.label) update.label = is.label;
      if (was.body !== is.body) update.body = is.body;
      if (wasAt.get(is.id) !== sortOrder) update.sortOrder = sortOrder;
      return Object.keys(update).length > 0 ? { id: is.id, update } : null;
    },
  });
}

/**
 * A descriptor for a bundle kind whose configuration is a title plus cards of
 * items.
 *
 * A card that only moved writes its `sortOrder` and nothing else, and its items
 * are not touched at all — which is the whole point of diffing two levels rather
 * than replacing the list.
 */
function createDescriptor<TCardCreate, TCardUpdate, TScalars>(
  spec: GeneratedBundleCardPayloadSpec<TCardCreate, TCardUpdate, TScalars>
): BundleSaveDescriptor {
  return {
    ownedFields: [spec.field],

    canWrite: (previous, next) =>
      canWriteOwnedConfig(spec.read, previous, next),

    diffConfiguration: ({
      previous,
      next,
      context,
    }: DiffBundleConfigurationArgs): BundleConfigurationPayload | null => {
      const before = spec.read(previous);
      const after = spec.read(next);
      // `canWrite` refused every other combination.
      if (!before || !after?.id) return null;

      const displayName =
        before.displayName === after.displayName
          ? null
          : after.displayName
            ? { displayName: after.displayName }
            : { clearDisplayName: true };

      const wasAt = new Map(
        before.cards.map((card, index) => [card.id, index])
      );
      const beforeById = new Map(before.cards.map((card) => [card.id, card]));

      const cards = mapKeyedListMutationOperations<
        TwoLevelCard,
        TCardCreate,
        TCardUpdate
      >({
        previousItems: before.cards,
        currentItems: after.cards,
        getId: serverRowId,
        toCreate: (card, sortOrder) =>
          spec.buildCardCreate({
            name: card.name,
            sortOrder,
            householdId: context.householdId,
            // A new card's items are all new too, whatever ids the form gave
            // them: there is no card for them to belong to yet.
            items: card.items.map((item, itemOrder) => ({
              create: {
                householdID: context.householdId,
                label: item.label,
                body: item.body,
                sortOrder: itemOrder,
              },
            })),
          }),
        toUpdate: ({ next: card, sortOrder }) => {
          const was = beforeById.get(card.id);
          if (!was) return null;

          const items = diffItems(was.items, card.items, context.householdId);
          const nameChanged = was.name !== card.name;
          const moved = wasAt.get(card.id) !== sortOrder;
          if (
            !nameChanged &&
            !moved &&
            isEmptyKeyedListMutationOperations(items)
          ) {
            return null;
          }

          return spec.buildCardUpdate({
            id: card.id,
            name: nameChanged ? card.name : undefined,
            sortOrder: moved ? sortOrder : undefined,
            items,
          });
        },
      });

      const scalars = spec.diffScalars?.(previous, next) ?? null;
      const itemsPerPage =
        before.itemsPerPage !== after.itemsPerPage &&
        after.itemsPerPage !== undefined
          ? { itemsPerPage: after.itemsPerPage }
          : null;
      if (
        !displayName &&
        !itemsPerPage &&
        !scalars &&
        isEmptyKeyedListMutationOperations(cards)
      ) {
        return null;
      }

      return spec.buildPayload({
        id: after.id,
        displayName,
        itemsPerPage,
        scalars,
        cardCreates: cards.creates,
        cardUpdates: cards.updates,
        removeCardIDs: cards.removeIDs,
      });
    },
  };
}

type GeneratedBundleCardKind =
  | PresentationBundleKind.AssetDistributionBundle
  | PresentationBundleKind.BusinessOverviewBundle
  | PresentationBundleKind.HealthcareDirectiveBundle
  | PresentationBundleKind.PowerOfAttorneyBundle
  | PresentationBundleKind.TrustOverviewBundle
  | PresentationBundleKind.WillBundle;

export interface GeneratedBundleCardSpec<
  TScalars extends object = Record<string, never>,
> {
  kind: GeneratedBundleCardKind;
  field: keyof ClientPresentationV2Bundle;
  read: (
    bundle: ClientPresentationV2Bundle
  ) => NormalizedTwoLevelConfig | undefined;
  diffScalars?: (
    previous: ClientPresentationV2Bundle,
    next: ClientPresentationV2Bundle
  ) => TScalars | null;
}

export function createGeneratedBundleCardDescriptor<
  TScalars extends object = Record<string, never>,
>(spec: GeneratedBundleCardSpec<TScalars>): BundleSaveDescriptor {
  switch (spec.kind) {
    case PresentationBundleKind.TrustOverviewBundle:
      return createDescriptor({
        ...spec,
        buildCardCreate: ({ name, sortOrder, householdId, items }) => ({
          create: { householdID: householdId, displayName: name, sortOrder },
          withTrustItems: items,
        }),
        buildCardUpdate: ({ id, name, sortOrder, items }) => ({
          id,
          update: {
            ...(name === undefined ? {} : { displayName: name }),
            ...(sortOrder === undefined ? {} : { sortOrder }),
            ...(items.removeIDs.length > 0
              ? { removeTrustItemIDs: items.removeIDs }
              : {}),
          },
          ...(items.creates.length > 0
            ? { withTrustItems: items.creates }
            : {}),
          ...(items.updates.length > 0
            ? { updateTrustItems: items.updates }
            : {}),
        }),
        buildPayload: ({
          id,
          displayName,
          itemsPerPage,
          cardCreates,
          cardUpdates,
          removeCardIDs,
        }) => ({
          updateTrustOverviewBundleConfiguration: {
            id,
            update: {
              ...displayName,
              ...itemsPerPage,
              ...(removeCardIDs.length > 0
                ? { removeTrustIDs: removeCardIDs }
                : {}),
            },
            ...(cardCreates.length > 0 ? { withTrusts: cardCreates } : {}),
            ...(cardUpdates.length > 0 ? { updateTrusts: cardUpdates } : {}),
          },
        }),
      });
    case PresentationBundleKind.BusinessOverviewBundle:
      return createDescriptor({
        ...spec,
        buildCardCreate: ({ name, sortOrder, householdId, items }) => ({
          create: { householdID: householdId, displayName: name, sortOrder },
          withBusinessItems: items,
        }),
        buildCardUpdate: ({ id, name, sortOrder, items }) => ({
          id,
          update: {
            ...(name === undefined ? {} : { displayName: name }),
            ...(sortOrder === undefined ? {} : { sortOrder }),
            ...(items.removeIDs.length > 0
              ? { removeBusinessItemIDs: items.removeIDs }
              : {}),
          },
          ...(items.creates.length > 0
            ? { withBusinessItems: items.creates }
            : {}),
          ...(items.updates.length > 0
            ? { updateBusinessItems: items.updates }
            : {}),
        }),
        buildPayload: ({
          id,
          displayName,
          itemsPerPage,
          cardCreates,
          cardUpdates,
          removeCardIDs,
        }) => ({
          updateBusinessOverviewBundleConfiguration: {
            id,
            update: {
              ...displayName,
              ...itemsPerPage,
              ...(removeCardIDs.length > 0
                ? { removeBusinessIDs: removeCardIDs }
                : {}),
            },
            ...(cardCreates.length > 0 ? { withBusinesses: cardCreates } : {}),
            ...(cardUpdates.length > 0
              ? { updateBusinesses: cardUpdates }
              : {}),
          },
        }),
      });
    case PresentationBundleKind.WillBundle:
      return createDescriptor({
        ...spec,
        buildCardCreate: ({ name, sortOrder, householdId, items }) => ({
          create: { householdID: householdId, client: name, sortOrder },
          withWillItems: items,
        }),
        buildCardUpdate: ({ id, name, sortOrder, items }) => ({
          id,
          update: {
            ...(name === undefined ? {} : { client: name }),
            ...(sortOrder === undefined ? {} : { sortOrder }),
            ...(items.removeIDs.length > 0
              ? { removeWillItemIDs: items.removeIDs }
              : {}),
          },
          ...(items.creates.length > 0 ? { withWillItems: items.creates } : {}),
          ...(items.updates.length > 0
            ? { updateWillItems: items.updates }
            : {}),
        }),
        buildPayload: ({
          id,
          displayName,
          itemsPerPage,
          cardCreates,
          cardUpdates,
          removeCardIDs,
        }) => ({
          updateWillBundleConfiguration: {
            id,
            update: {
              ...displayName,
              ...itemsPerPage,
              ...(removeCardIDs.length > 0
                ? { removeClientIDs: removeCardIDs }
                : {}),
            },
            ...(cardCreates.length > 0 ? { withClients: cardCreates } : {}),
            ...(cardUpdates.length > 0 ? { updateClients: cardUpdates } : {}),
          },
        }),
      });
    case PresentationBundleKind.PowerOfAttorneyBundle:
      return createDescriptor({
        ...spec,
        buildCardCreate: ({ name, sortOrder, householdId, items }) => ({
          create: { householdID: householdId, client: name, sortOrder },
          withPowerOfAttorneyItems: items,
        }),
        buildCardUpdate: ({ id, name, sortOrder, items }) => ({
          id,
          update: {
            ...(name === undefined ? {} : { client: name }),
            ...(sortOrder === undefined ? {} : { sortOrder }),
            ...(items.removeIDs.length > 0
              ? { removePowerOfAttorneyItemIDs: items.removeIDs }
              : {}),
          },
          ...(items.creates.length > 0
            ? { withPowerOfAttorneyItems: items.creates }
            : {}),
          ...(items.updates.length > 0
            ? { updatePowerOfAttorneyItems: items.updates }
            : {}),
        }),
        buildPayload: ({
          id,
          displayName,
          itemsPerPage,
          cardCreates,
          cardUpdates,
          removeCardIDs,
        }) => ({
          updatePowerOfAttorneyBundleConfiguration: {
            id,
            update: {
              ...displayName,
              ...itemsPerPage,
              ...(removeCardIDs.length > 0
                ? { removeClientIDs: removeCardIDs }
                : {}),
            },
            ...(cardCreates.length > 0 ? { withClients: cardCreates } : {}),
            ...(cardUpdates.length > 0 ? { updateClients: cardUpdates } : {}),
          },
        }),
      });
    case PresentationBundleKind.HealthcareDirectiveBundle:
      return createDescriptor({
        ...spec,
        buildCardCreate: ({ name, sortOrder, householdId, items }) => ({
          create: { householdID: householdId, client: name, sortOrder },
          withHealthcareDirectiveItems: items,
        }),
        buildCardUpdate: ({ id, name, sortOrder, items }) => ({
          id,
          update: {
            ...(name === undefined ? {} : { client: name }),
            ...(sortOrder === undefined ? {} : { sortOrder }),
            ...(items.removeIDs.length > 0
              ? { removeHealthcareDirectiveItemIDs: items.removeIDs }
              : {}),
          },
          ...(items.creates.length > 0
            ? { withHealthcareDirectiveItems: items.creates }
            : {}),
          ...(items.updates.length > 0
            ? { updateHealthcareDirectiveItems: items.updates }
            : {}),
        }),
        buildPayload: ({
          id,
          displayName,
          itemsPerPage,
          cardCreates,
          cardUpdates,
          removeCardIDs,
        }) => ({
          updateHealthcareDirectiveBundleConfiguration: {
            id,
            update: {
              ...displayName,
              ...itemsPerPage,
              ...(removeCardIDs.length > 0
                ? { removeClientIDs: removeCardIDs }
                : {}),
            },
            ...(cardCreates.length > 0 ? { withClients: cardCreates } : {}),
            ...(cardUpdates.length > 0 ? { updateClients: cardUpdates } : {}),
          },
        }),
      });
    case PresentationBundleKind.AssetDistributionBundle:
      return createDescriptor({
        ...spec,
        buildCardCreate: ({ name, sortOrder, householdId, items }) => ({
          create: { householdID: householdId, displayName: name, sortOrder },
          withItemDetails: items,
        }),
        buildCardUpdate: ({ id, name, sortOrder, items }) => ({
          id,
          update: {
            ...(name === undefined ? {} : { displayName: name }),
            ...(sortOrder === undefined ? {} : { sortOrder }),
            ...(items.removeIDs.length > 0
              ? { removeItemDetailIDs: items.removeIDs }
              : {}),
          },
          ...(items.creates.length > 0
            ? { withItemDetails: items.creates }
            : {}),
          ...(items.updates.length > 0
            ? { updateItemDetails: items.updates }
            : {}),
        }),
        buildPayload: ({
          id,
          displayName,
          itemsPerPage,
          scalars,
          cardCreates,
          cardUpdates,
          removeCardIDs,
        }) => ({
          updateAssetDistributionBundleConfiguration: {
            id,
            update: {
              ...displayName,
              ...itemsPerPage,
              ...scalars,
              ...(removeCardIDs.length > 0
                ? { removeDistributionItemIDs: removeCardIDs }
                : {}),
            },
            ...(cardCreates.length > 0
              ? { withDistributionItems: cardCreates }
              : {}),
            ...(cardUpdates.length > 0
              ? { updateDistributionItems: cardUpdates }
              : {}),
          },
        }),
      });
  }
}
